/**
 * Mapping Type Definitions
 *
 * Types for mapping source tables to BM classes.
 */

// ============================================================================
// Branded Types (IDs)
// ============================================================================

/** Mapping ID (branded type) */
export type TMappingId = string & { readonly __brand: "MappingId" };

/** Mapping Source ID (branded type) */
export type TMappingSourceId = string & { readonly __brand: "MappingSourceId" };

/** Mapping Rule ID (branded type) */
export type TMappingRuleId = string & { readonly __brand: "MappingRuleId" };

/** Mapping Stage ID (branded type) */
export type TMappingStageId = string & { readonly __brand: "MappingStageId" };

/** Mapping Pipeline Node ID (branded type) */
export type TMappingPipelineNodeId = string & {
  readonly __brand: "MappingPipelineNodeId";
};

/** Mapping Pipeline Field ID (branded type) */
export type TMappingPipelineFieldId = string & {
  readonly __brand: "MappingPipelineFieldId";
};

// ============================================================================
// Helper Functions
// ============================================================================

export function toMappingId(id: number | string): TMappingId {
  return String(id) as TMappingId;
}

export function toMappingSourceId(id: number | string): TMappingSourceId {
  return String(id) as TMappingSourceId;
}

export function toMappingRuleId(id: number | string): TMappingRuleId {
  return String(id) as TMappingRuleId;
}

export function toMappingStageId(id: number | string): TMappingStageId {
  return String(id) as TMappingStageId;
}

export function toMappingPipelineNodeId(
  id: number | string
): TMappingPipelineNodeId {
  return String(id) as TMappingPipelineNodeId;
}

export function toMappingPipelineFieldId(
  id: number | string
): TMappingPipelineFieldId {
  return String(id) as TMappingPipelineFieldId;
}

// ============================================================================
// Enums
// ============================================================================

/** Mapping source type */
export type TMappingSourceType = "table" | "api" | "csv";

/** Cursor type for incremental_column capture strategy */
export type TCursorType = "timestamp" | "numeric" | "batch_id";

/** CDC stream format */
export type TCdcFormat = "wal2json" | "debezium";

/**
 * Incremental capture strategy: discriminated union describing how to pick up
 * incremental data from a mapping source table.
 *
 * Serialization contract (backend DTO):
 *   { incrementalCapture: <kind>, incrementalConfig: <object|null> }
 */
export type TIncrementalCaptureStrategy =
  | { readonly kind: "full_refresh" }
  | {
      readonly kind: "incremental_column";
      readonly cursorColumn: string;
      readonly cursorType: TCursorType;
      readonly lookbackWindow?: string;
    }
  | {
      readonly kind: "log_table";
      readonly logTableEntityId: number;
      readonly operationColumn: string;
      readonly timestampColumn: string;
      readonly keyColumns: readonly string[];
    }
  | {
      readonly kind: "cdc_stream";
      readonly slotName: string;
      readonly format: TCdcFormat;
    }
  | { readonly kind: "custom"; readonly whereTemplate: string };

/**
 * Причина, по которой сохранённая настройка захвата не разобрана.
 *
 * Норма: spec/features/mapping/capture-strategy.md, R22 — неразобранная
 * конфигурация показывается как ошибка загрузки настройки.
 */
export type TCaptureParseFailure =
  /** Название стратегии продукту неизвестно. */
  | "unknown_strategy"
  /** Стратегия требует конфигурацию, а её нет. */
  | "config_missing"
  /** Конфигурация не соответствует набору параметров своей стратегии. */
  | "config_mismatch";

/**
 * Сохранённая настройка захвата, которую продукт разобрать не смог.
 *
 * Норма: capture-strategy-R22 — такая настройка показывается ошибкой,
 * а **не** подменяется полной перезагрузкой.
 */
export interface IUnparsedCaptureStrategy {
  readonly kind: "unparsed";
  /** Название стратегии как пришло от продукта. */
  readonly capture: string;
  /** Машиночитаемая причина — по ней собирается текст пользователю. */
  readonly reason: TCaptureParseFailure;
}

/**
 * Что у источника показывается как способ получения данных: разобранная
 * стратегия либо ошибка разбора сохранённой настройки (R22).
 */
export type TStoredCaptureStrategy =
  | TIncrementalCaptureStrategy
  | IUnparsedCaptureStrategy;

// ============================================================================
// Entities
// ============================================================================

/** Mapping entity */
export interface IMapping {
  readonly id: TMappingId;
  readonly stableId: string;
  readonly featureId: number;
  readonly name: string;
  readonly autoName: string;
  readonly description?: string;
  readonly targetClassStableId: string;
  readonly targetAlias?: string;
  readonly createdAt: string;
  readonly updatedAt: string;
}

/** Mapping source entity */
export interface IMappingSource {
  readonly id: TMappingSourceId;
  readonly mappingId: TMappingId;
  readonly sourceType: TMappingSourceType;
  readonly sourceEntityId?: number;
  readonly tableEntityId?: number;
  readonly tableName?: string;
  readonly tableComment?: string;
  readonly sourceName?: string;
  readonly providerName?: string;
  readonly isPrimary: boolean;
  readonly joinCondition?: string;
  readonly ordinal: number;
  readonly alias?: string;
  readonly nodeId?: TMappingPipelineNodeId;
  readonly stageId?: TMappingStageId;
  readonly branchIndex?: number;
  /**
   * Способ получения данных: разобранная стратегия (по умолчанию
   * `{ kind: "full_refresh" }`) либо ошибка разбора сохранённой настройки
   * (`{ kind: "unparsed" }`, capture-strategy-R22).
   */
  readonly incrementalCapture: TStoredCaptureStrategy;
  /** Optional custom WHERE clause override */
  readonly whereOverride?: string;
  /**
   * Признак «источник не подтверждает» (spec/features/mapping/sources.md, R04):
   * объекта таблицы больше нет, а источник сохранён — расхождение разрешает
   * человек, убрав источник либо переназначив ему таблицу.
   */
  readonly isUnconfirmed?: boolean;
}

/** Mapping with all sources */
export interface IMappingWithSources extends IMapping {
  readonly sources: readonly IMappingSource[];
}

/** Mapping summary for tree view */
export interface IMappingSummary {
  readonly id: TMappingId;
  readonly stableId: string;
  readonly name: string;
  readonly autoName: string;
  readonly targetClassStableId: string;
  readonly sourceCount: number;
  /**
   * Таблицы, которыми маппинг пользуется как источниками.
   *
   * Норма: mapping-mappings-R15 — под узлом таблицы показываются только
   * маппинги фичи, использующие ЭТУ таблицу источником, и состав узла сходится
   * со счётчиком (R11).
   */
  readonly sourceTableEntityIds?: readonly number[];
}

// ============================================================================
// Aggregations (for tree badges)
// ============================================================================

/** Count of mappings per table */
export interface IMappingCountByTable {
  readonly tableEntityId: number;
  readonly count: number;
}

/** Count of mappings per class */
export interface IMappingCountByClass {
  readonly classStableId: string;
  readonly count: number;
}

/** Has mappings response */
export interface IHasMappingsResponse {
  readonly hasMappings: boolean;
}

// ============================================================================
// Request Types
// ============================================================================

/** Create mapping request */
export interface ICreateMappingRequest {
  readonly name: string;
  readonly targetClassStableId: string;
  readonly description?: string;
  readonly primaryTableEntityId: number;
}

/** Update mapping request */
export interface IUpdateMappingRequest {
  readonly name?: string;
  readonly description?: string;
  readonly targetAlias?: string;
}

/** Update mapping source request */
export interface IUpdateMappingSourceRequest {
  readonly alias?: string;
  readonly incrementalCapture?: string;
  readonly incrementalConfig?: Record<string, unknown> | null;
  readonly whereOverride?: string | null;
}

/** Add mapping source request */
export interface IAddMappingSourceRequest {
  readonly tableEntityId: number;
  readonly joinCondition?: string;
  readonly incrementalCapture?: string;
  readonly incrementalConfig?: Record<string, unknown> | null;
  readonly whereOverride?: string | null;
}

// ============================================================================
// Mapping Rules
// ============================================================================

/** Mapping rule entity */
export interface IMappingRule {
  readonly id: TMappingRuleId;
  readonly mappingId: TMappingId;
  readonly inputFieldId?: TMappingPipelineFieldId;
  readonly mappingSourceId?: TMappingSourceId;
  readonly sourceColumnName?: string;
  readonly sourceExpression?: string;
  readonly targetFieldStableId?: string;
  readonly targetClassStableId?: string;
  readonly transformExpression?: string;
  readonly ordinal: number;
  readonly createdAt: string;
  readonly updatedAt: string;
}

/** Create mapping rule request */
export interface ICreateMappingRuleRequest {
  readonly inputFieldId?: number;
  readonly mappingSourceId?: number;
  readonly sourceColumnName?: string;
  readonly sourceExpression?: string;
  readonly targetFieldStableId?: string;
  readonly targetClassStableId?: string;
  readonly transformExpression?: string;
}

/** Update mapping rule request */
export interface IUpdateMappingRuleRequest {
  readonly inputFieldId?: number | null;
  readonly mappingSourceId?: number | null;
  readonly sourceColumnName?: string | null;
  readonly sourceExpression?: string | null;
  readonly targetFieldStableId?: string | null;
  readonly targetClassStableId?: string | null;
  readonly transformExpression?: string | null;
}

/** Reorder mapping rules request */
export interface IReorderMappingRulesRequest {
  readonly ruleIds: readonly number[];
}
